import { Component, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material';
import { EnquiryComponent } from '../enquiry/enquiry.component';
import { LocationMappingService } from '../services/location-mapping.service';

export interface Testimonial {
  name: string;
  course: string;
  rating: number;
  review: string;
  photo?: string;
  company?: string;
  designation?: string;
}

@Component({
  selector: 'app-testimonials',
  template: `
    <div class="testimonials-section">
      <div class="section-header">
        <span class="section-badge">Testimonials</span>
        <h2 class="section-title">What Our Students Say</h2>
        <p class="section-subtitle">Real stories from students who transformed their careers with NICT</p>
      </div>
      
      <div class="testimonials-grid">
        <div class="testimonial-card" *ngFor="let testimonial of testimonials">
          <div class="quote-icon">
            <i class="fa fa-quote-left"></i>
          </div>
          
          <div class="rating">
            <span class="star filled" *ngFor="let star of getStars(testimonial.rating)">★</span>
            <span class="star" *ngFor="let star of getEmptyStars(testimonial.rating)">★</span>
          </div>
          
          <p class="review">{{ testimonial.review }}</p>
          
          <div class="student-info">
            <div class="avatar">
              <i class="fa fa-user-circle"></i>
            </div>
            <div class="student-details">
              <h4 class="student-name">{{ testimonial.name }}</h4>
              <p class="student-course">
                <i class="fa fa-graduation-cap"></i> {{ testimonial.course }}
              </p>
              <p class="student-company" *ngIf="testimonial.company">
                <i class="fa fa-briefcase"></i> {{ testimonial.designation }} @ {{ testimonial.company }}
              </p>
            </div>
          </div>
          
          <div class="verified-badge">
            <i class="fa fa-check-circle"></i> Verified Student
          </div>
        </div>
      </div>

      <div class="cta-section">
        <p class="cta-text">Join thousands of successful students!</p>
        <button class="cta-button" (click)="openEnquiry()">
          <span>Start Your Journey</span>
          <i class="fa fa-arrow-right"></i>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .testimonials-section {
      padding: 80px 20px;
      background: #0a2540;
      position: relative;
      overflow: hidden;
    }

    .testimonials-section::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="2" fill="white" opacity="0.1"/></svg>');
      pointer-events: none;
    }

    .section-header {
      text-align: center;
      margin-bottom: 60px;
      position: relative;
      z-index: 1;
    }

    .section-badge {
      display: inline-block;
      padding: 8px 20px;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
      border-radius: 50px;
      color: white;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 20px;
      border: 1px solid rgba(255, 255, 255, 0.3);
    }

    .section-title {
      font-size: 42px;
      font-weight: bold;
      color: white;
      margin: 0 0 15px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
    }

    .section-subtitle {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
      max-width: 600px;
      margin: 0 auto;
    }

    .testimonials-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      gap: 30px;
      max-width: 1400px;
      margin: 0 auto 80px;
      position: relative;
      z-index: 1;
    }

    .testimonial-card {
      background: #16213e;
      border-radius: 24px;
      padding: 40px 32px;
      box-shadow: 0 8px 32px rgba(13,29,49,0.12);
      transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      position: relative;
      overflow: hidden;
      color: #fff;
      border: 1.5px solid #233554;
    }

    .testimonial-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: #1565c0;
    }

    .testimonial-card:hover {
      transform: translateY(-10px) scale(1.02);
      box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    }

    .quote-icon {
      position: absolute;
      top: 25px;
      right: 25px;
      font-size: 40px;
      color: rgba(13, 29, 49, 0.18);
    }

    .rating {
      font-size: 22px;
      margin-bottom: 20px;
      letter-spacing: 2px;
    }

    .star.filled {
      color: #ffc107;
      text-shadow: 0 2px 4px rgba(255, 193, 7, 0.3);
    }

    .star:not(.filled) {
      color: #e0e0e0;
    }

    .review {
      font-size: 17px;
      line-height: 1.8;
      color: #e3e3e3;
      margin-bottom: 25px;
      font-style: italic;
      position: relative;
      padding-left: 18px;
      border-left: 3px solid #1565c0;
      min-height: 100px;
    }

    .student-info {
      display: flex;
      align-items: center;
      gap: 15px;
      padding-top: 20px;
      border-top: 1px solid #f0f0f0;
    }

    .avatar {
      font-size: 50px;
      color: #1565c0;
      flex-shrink: 0;
    }

    .student-details {
      flex: 1;
    }

    .student-name {
      font-size: 18px;
      font-weight: 700;
      color: #fff;
      margin: 0 0 8px;
    }

    .student-course {
      font-size: 14px;
      color: #90caf9;
      margin: 5px 0;
      font-weight: 600;
    }

    .student-course i,
    .student-company i {
      margin-right: 5px;
      opacity: 0.7;
    }

    .student-company {
      font-size: 13px;
      color: #666;
      margin: 5px 0 0;
    }

    .verified-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: #e8f5e9;
      color: #2e7d32;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      margin-top: 15px;
    }

    .verified-badge i {
      font-size: 14px;
    }

    .cta-section {
      text-align: center;
      margin-top: 60px;
      position: relative;
      z-index: 1;
    }

    .cta-text {
      font-size: 24px;
      color: white;
      font-weight: 600;
      margin-bottom: 25px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
    }

    .cta-button {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      background: white;
      color: #1976d2;
      border: none;
      padding: 18px 40px;
      border-radius: 50px;
      font-size: 18px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 8px 25px rgba(0,0,0,0.2);
    }

    .cta-button:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 35px rgba(0,0,0,0.3);
      background: #f8f9fa;
    }

    .cta-button i {
      transition: transform 0.3s ease;
    }

    .cta-button:hover i {
      transform: translateX(5px);
    }

    @media (max-width: 768px) {
      .testimonials-section {
        padding: 60px 15px;
      }

      .section-title {
        font-size: 32px;
      }

      .section-subtitle {
        font-size: 16px;
      }

      .testimonials-grid {
        grid-template-columns: 1fr;
        gap: 20px;
        margin-bottom: 50px;
      }

      .testimonial-card {
        padding: 25px;
      }

      .review {
        font-size: 15px;
        min-height: auto;
      }

      .trust-badges {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        padding: 30px 20px;
      }

      .badge-number {
        font-size: 36px;
      }

      .badge-label {
        font-size: 14px;
      }

      .cta-text {
        font-size: 20px;
      }

      .cta-button {
        padding: 15px 30px;
        font-size: 16px;
      }
    }
  `]
})
export class TestimonialsComponent implements OnInit {
  
  testimonials: Testimonial[] = [
    {
      name: "Priya Sharma",
      course: "Full Stack Development (MERN)",
      rating: 5,
      review: "NICT transformed my career! The hands-on training and job assistance helped me land a developer role at a top IT company. The instructors are knowledgeable and always ready to help.",
      company: "Infosys",
      designation: "Software Developer"
    },
    {
      name: "Rajesh Kumar",
      course: "Digital Marketing with AI",
      rating: 5,
      review: "Best decision I made for my career. The practical approach and real-world projects gave me the confidence to start my own digital marketing agency. Highly recommend NICT!",
      company: "Own Agency",
      designation: "Founder"
    },
    {
      name: "Sneha Reddy",
      course: "Data Science",
      rating: 5,
      review: "The course content is up-to-date and industry-relevant. I got placed in a data analyst role within 2 months of completing the course. Thank you NICT team!",
      company: "Amazon",
      designation: "Data Analyst"
    },
    {
      name: "Amit Patel",
      course: "SAP FICO",
      rating: 5,
      review: "Excellent training institute with experienced faculty. The flexible batch timings made it easy for me to learn while working. Got a 40% salary hike after certification.",
      company: "Wipro",
      designation: "SAP Consultant"
    },
    {
      name: "Lakshmi Iyer",
      course: "Tally Prime with GST",
      rating: 5,
      review: "Very affordable fees and quality training. The practical sessions on real accounting scenarios were extremely helpful. Now working as an accountant in a reputed firm.",
      company: "Deloitte",
      designation: "Accountant"
    },
    {
      name: "Karthik Rao",
      course: "Advanced Excel with AI",
      rating: 5,
      review: "The instructors at NICT are amazing! They made complex Excel functions easy to understand. This course boosted my productivity at work significantly.",
      company: "TCS",
      designation: "Business Analyst"
    }
  ];

  constructor(
    private bottomSheet: MatBottomSheet,
    private locationMappingService: LocationMappingService
  ) { }

  ngOnInit(): void {
    this.addTestimonialSchema();
  }

  getStars(rating: number): any[] {
    return Array(rating).fill(0);
  }

  getEmptyStars(rating: number): any[] {
    return Array(5 - rating).fill(0);
  }

  openEnquiry(): void {
    // Get location from localStorage if available
    const storedLocation = this.locationMappingService.getStoredLocation();
    
    this.bottomSheet.open(EnquiryComponent, {
      data: {
        source: "Testimonials Section",
        location: storedLocation
      },
      disableClose: false
    });
  }

  addTestimonialSchema(): void {
    const reviews = this.testimonials.map(t => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating,
        "bestRating": "5"
      },
      "reviewBody": t.review
    }));

    const schema = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "NICT Computer Education",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": this.testimonials.length
      },
      "review": reviews
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  }
}
